import { nanoid } from 'nanoid'
import makeCircle from './smallestEnclosingCircle'
import { orderTypes, appTypeConfig } from '../../config'
import { intersectTwoCircles } from './intersectTwoCircles'
import { radiansToDegrees } from './radDeg'
import getIsOmegaLensCtoCapable from './getIsOmegaLensCtoCapable'

// obsolete - use safetyMargins from appTypeConfi, now differently for both calculations
// const safetyMargin = 1.5

const log = console.getLogger('VCA')

export function toRad(deg) {
  return deg * (Math.PI / 180)
}

export function bothSides(fn, data, ...args) {
  return {
    ...data,
    r: fn(data.r, ...args),
    l: fn(data.l, ...args),
  }
}

export function radiiToXY(data) {
  const angles = data.length
  const oneAngle = (Math.PI * 2) / angles // radians
  let currentAngle = 0

  return data.map(d => {
    const r = d.r || d
    const res = {
      r: d.r || d,
      x: Math.cos(currentAngle) * r,
      y: Math.sin(currentAngle) * r,
    }
    currentAngle -= oneAngle
    return typeof d === 'object' ? { ...d, ...res } : res
  })
}

// when radii are not equally distributed, used for PMS import (b2b xml)
// data = [{ angle, radius  }] angle is in deg
export function explicitRadiiToXY(data) {
  return data.map(d => {
    const r = d.radius
    if (d.angle && typeof d.angle._ !== 'undefined') d.angle = d.angle._
    const angle = (360 - d.angle) * (Math.PI / 180)
    console.log('explicitRadiiToXY', r, d.angle, angle, Math.cos(angle) * r, Math.sin(angle) * r)
    const res = {
      r,
      x: Math.cos(angle) * r,
      y: Math.sin(angle) * r,
      angleDeg: d.angle,
      angle,
    }
    return res
  })
}

export function xyToRadii(data) {
  const angles = data.length
  const oneAngle = (Math.PI * 2) / angles
  // make line from center under current angle to big point, lets say 500 mm from start
  // make line between every point in XY set
  // iterate on it, when they intersect - its the line we are looking for
  // find out where the intersection happens --> create new point
  // calculate length from center to this point
  const linesBetweenPoints = []
  data.forEach((d, index) => {
    let nextIndex = index + 1
    if (index === data.length - 1) {
      nextIndex = 0
    }
    linesBetweenPoints.push({
      x1: d.x,
      y1: d.y,
      x2: data[nextIndex].x,
      y2: data[nextIndex].y,
    })
  })

  let currentAngle = 0
  return data.map((d, index) => {
    let foundPointX = 0
    let foundPointY = 0
    const centerLineEnd = {
      x2: Math.cos(currentAngle) * 5000,
      y2: Math.sin(currentAngle) * 5000,
    }

    let desiredLine = null
    for (let i = 0; i < linesBetweenPoints.length; i++) {
      const edgeLine = linesBetweenPoints[i]
      if (
        intersectWhereNew(
          0,
          0,
          centerLineEnd.x2,
          centerLineEnd.y2,
          edgeLine.x1,
          edgeLine.y1,
          edgeLine.x2,
          edgeLine.y2,
        )
      ) {
        desiredLine = edgeLine
        break
      }
    }
    if (!desiredLine) {
      // probably axis centers, or point itself
      foundPointX = d.x
      foundPointY = d.y
    } else {
      const resultedPosition = intersectWhereNew(
        0,
        0,
        centerLineEnd.x2,
        centerLineEnd.y2,
        desiredLine.x1,
        desiredLine.y1,
        desiredLine.x2,
        desiredLine.y2,
      )

      foundPointX = resultedPosition.x
      foundPointY = resultedPosition.y
    }

    const res = Math.round(Math.sqrt(foundPointX ** 2 + foundPointY ** 2) * 1000000) / 1000000
    // const res = Math.sqrt((foundPointX ** 2) + (foundPointY ** 2))
    currentAngle -= oneAngle
    return res
  })
}

export function process3d(points) {
  return points.map(p => {
    const res = {
      ...p,
      drawAngle: -p.angle,
      x: Math.cos((-p.angle * Math.PI) / 180) * p.diameter,
      y: Math.sin((-p.angle * Math.PI) / 180) * p.diameter,
      thickness: Math.abs(Math.abs(p.depth1) - Math.abs(p.depth2)),
    }
    return res
  })
}

export function findBoxSystemXY(data) {
  let maxX = 0
  let minX = 0
  let maxY = 0
  let minY = 0
  data.forEach(d => {
    maxX = d.x > maxX ? d.x : maxX
    minX = d.x < minX ? d.x : minX
    maxY = d.y > maxY ? d.y : maxY
    minY = d.y < minY ? d.y : minY
  })

  return {
    maxX,
    minX,
    maxY,
    minY,
    hbox: Math.abs(maxX) + Math.abs(minX),
    vbox: Math.abs(maxY) + Math.abs(minY),
  }
}

export function findSmallestCircle(xyData) {
  return makeCircle(xyData)
}

// new center recognition via box system. It used to be smallest enclosing circle
export function centerXYData(xyData) {
  const { minX, minY, maxX, maxY } = xyData.reduce(
    ({ minX, minY, maxX, maxY }, point) => {
      if (point.x < minX) {
        minX = point.x
      }
      if (point.x > maxX) {
        maxX = point.x
      }
      if (point.y < minY) {
        minY = point.y
      }
      if (point.y > maxY) {
        maxY = point.y
      }

      return { minX, minY, maxX, maxY }
    },
    { minX: 0, minY: 0, maxX: 0, maxY: 0 },
  )

  const diffY = maxY - (Math.abs(minY) + Math.abs(maxY)) / 2
  const diffX = maxX - (Math.abs(minX) + Math.abs(maxX)) / 2

  xyData = xyData.map(p => ({
    ...p,
    x: p.x - diffX,
    y: p.y - diffY,
  }))
  return xyData
}

// if some data is missing or invalid, calculate them
export function recalcVca({ vca: data, orderValues = {} }) {
  let { shapeData, shapeDataXY, invalidRadii, invalidXY } = data

  shapeData = !shapeData || invalidRadii ? bothSides(xyToRadii, shapeDataXY) : shapeData

  shapeDataXY = !shapeDataXY || invalidXY ? bothSides(radiiToXY, shapeData) : shapeDataXY

  const box = bothSides(findBoxSystemXY, shapeDataXY)

  // algo for finding the smallest enclosing circe
  // this is for RX lenses (custom made, RX, design shift = 1)
  const isRightShapeEmpty = !shapeData.r || shapeData.r.length === 0
  const circle = isRightShapeEmpty ? makeCircle(shapeDataXY.l) : makeCircle(shapeDataXY.r)

  // console.log('!!!orderValues in calcStockRadii', orderValues)

  // novinka: stockRadius se pocita on demand
  // we want to calc circle more sophistically (excel), but only for browser (depends on pd/height/dbl)
  // let circles = null

  // // with fallbacks to vca centration data
  // const centrationValues = {
  //   pdR: orderValues.pdR || data.pdR,
  //   pdL: orderValues.pdL || data.pdL,
  //   heightR: orderValues.heightR || data.heightR,
  //   heightL: orderValues.heightL || data.heightL,
  // }

  // if (process.env.IS_BROWSER) {
  //   // this is for stock lenses (designShift = 0)
  //   circles = calcStockRadii({
  //     ...data,
  //     ...centrationValues,
  //     shapeDataXY,
  //     box,
  //     ed: 2 * circle.r,
  //   })
  // }

  // log.debug(
  //   'safetyMarginManufacturer and Stock',
  //   appTypeConfig.safetyMarginManufacturer,
  //   appTypeConfig.safetyMarginStock,
  // )

  // log.debug('smallestEnclosingCircle', circle)

  return {
    ...data,
    shapeData,
    shapeDataXY,
    invalidRadii: false,
    invalidXY: false,
    smallestEnclosingCircleRadius: circle.r, // smallest enclosing circle
    smallestEnclosingCircleX: circle.x,
    smallestEnclosingCircleY: circle.y,
    box,
  }
}

export function deformVertical(data, vbox) {
  if (!vbox || isNaN(vbox)) {
    return data
  }

  data = {
    ...data,
  }
  delete data.shapeData // delete it, and it recalc automatically
  if (vbox > 0) {
    const shapeDataXY = bothSides(
      deformVerticalOneSide,
      {
        r: {
          shapeDataXY: data.shapeDataXY.r,
          box: data.box.r,
        },
        l: {
          shapeDataXY: data.shapeDataXY.l,
          box: data.box.l,
        },
      },
      data,
      vbox,
    )

    data = {
      ...data,
      shapeDataXY,
    }
  }
  return recalcVca({ vca: data })
}

function deformVerticalOneSide(sideData, data, vbox) {
  const { shapeDataXY, box } = sideData

  // minimum change to desired VBOX (usually distance from top/bottom edge to desired vbox edge)
  const desiredDelta = (vbox - box.vbox) / 2

  return shapeDataXY.map((d, index) => {
    // take current vbox and create ratio from current Y coord
    // the closer y is to the current edge, the more we move the point. More far means we move it less
    const smoothing = Math.abs(d.y) / (box.vbox / 2)

    const add = desiredDelta * smoothing

    // if ([0, 90, 180, 270].includes(index)) {
    //   console.log(index, 'add - currDelta', 'desiredDelta', desiredDelta, 'd.y', d.y, 'ratio', smoothing)
    // }
    return {
      x: d.x,
      y: d.y + (index < shapeDataXY.length / 2 ? -add : +add),
    }
  })
}

export function deformHorizontal(data, hbox) {
  if (!hbox || isNaN(hbox)) {
    return data
  }

  data = { ...data }
  delete data.shapeData // delete it, and it recalc automatically
  if (hbox > 0) {
    const shapeDataXY = bothSides(
      deformHorizontalOneSide,
      {
        r: {
          shapeDataXY: data.shapeDataXY.r,
          box: data.box.r,
        },
        l: {
          shapeDataXY: data.shapeDataXY.l,
          box: data.box.l,
        },
      },
      data,
      hbox,
    )

    data = {
      ...data,
      shapeDataXY,
    }
  }

  return recalcVca({ vca: data })
}

function deformHorizontalOneSide(sideData, data, hbox) {
  const { shapeDataXY, box } = sideData

  // minimum change to desired hBOX (usually distance from top/bottom edge to desired hbox edge)
  const desiredDelta = (hbox - box.hbox) / 2

  return shapeDataXY.map((d, index) => {
    // take current hbox and create ratio from current Y coord
    // the closer y is to the current edge, the more we move the point. More far means we move it less
    const smoothing = Math.abs(d.x) / (box.hbox / 2)

    const add = desiredDelta * smoothing

    // if ([0, 90, 180, 270].includes(index)) {
    //   console.log(index, 'add - currDelta', 'desiredDelta', desiredDelta, 'd.y', d.y, 'ratio', smoothing)
    // }
    return {
      x:
        d.x +
        (index < shapeDataXY.length / 4 || index > (shapeDataXY.length / 4) * 3 ? +add : -add),
      // x: d.x + add,
      y: d.y,
    }
  })
}

export function deformABCD(data, values) {
  data = { ...data }
  delete data.shapeData // delete it, and it recalc automatically

  const shapeDataXY = bothSides(
    deformABCDOneSide,
    {
      r: {
        shapeDataXY: data.shapeDataXY.r,
        box: data.box.r,
        isRight: true,
      },
      l: {
        shapeDataXY: data.shapeDataXY.l,
        box: data.box.l,
      },
    },
    data,
    values,
  )

  delete data.box // delete it, and it recalc automatically
  delete data.smallestEnclosingCircle // delete it, and it recalc automatically

  data = {
    ...data,
    shapeDataXY,
  }

  return data
}

function deformABCDOneSide(sideData, data, { top, left, right, bottom }) {
  const { shapeDataXY, box, isRight } = sideData

  // minimum change to desired hBOX (usually distance from top/bottom edge to desired hbox edge)
  // const desiredDeltaVerical = (hbox - box.hbox) / 2

  return shapeDataXY.map((d, index) => {
    // if (index ==0 || index == 90)
    // console.log('isRig', isRight, d, index)
    // same as box deformation
    const smoothingRight = Math.abs(d.x) / Math.abs(box.maxX)
    const smoothingTop = Math.abs(d.y) / Math.abs(box.minY)
    const smoothingLeft = Math.abs(d.x) / Math.abs(box.minX)
    const smoothingBottom = Math.abs(d.y) / Math.abs(box.maxY)

    let addRight = right * smoothingRight
    const addTop = -top * smoothingTop
    let addLeft = -left * smoothingLeft
    const addBottom = bottom * smoothingBottom

    let addX =
      index < shapeDataXY.length / 4 || index > (shapeDataXY.length / 4) * 3 ? addRight : addLeft

    if (!isRight) {
      addRight = -right * smoothingLeft
      addLeft = left * smoothingRight
      addX =
        index > shapeDataXY.length / 4 && index < (shapeDataXY.length / 4) * 3 ? addRight : addLeft
    }

    return {
      x: d.x + addX,
      y: d.y + (index < shapeDataXY.length / 2 ? addTop : addBottom),
    }
  })
}

export const getMultifocalConfig = (lens, selectedProgLength) => {
  if (!lens || !lens.progressiveGeo) {
    return null
  }
  const { prpToDrp, prpToLrp, prpToNrp } = lens.progressiveGeo

  // calculated prpToNrpUp - this value is affected by selected corridor
  // this works for Omega and LTL lenses, but it is not universal equation
  // progressive length = corridor - 3
  // progressive length is distance between prpToLrp.up and prpToNrp.up
  let prpToNrpUp = prpToNrp.up
  // for now enabled only for reize. Can be different for other manufacturers
  if (appTypeConfig.APP_TYPE === 'reize') {
    if (selectedProgLength) {
      prpToNrpUp = -(selectedProgLength - prpToLrp.up)
    }
  }

  // our pupil (height + PD) is LRP, so find PRP
  const prp = { in: -prpToLrp.in, up: prpToLrp.up }

  const conf = {
    upX: prp.in + prpToDrp.in,
    upY: prp.up - prpToDrp.up,
    downX: prp.in + prpToNrp.in,
    downY: prp.up - prpToNrpUp,
  }
  // console.log('prp', prp, prpToLrp, prpToDrp, prpToNrp, conf)
  return conf
}

export const getBifocalConfig = lens => {
  if (!lens?.bifocalGeo || !appTypeConfig.isBifocalVisualisationEnabled) {
    return null
  }
  const { bgcToPrp, prpToLrp, prpToNrp, nearPortion } = lens.bifocalGeo
  // we are not using NRP since the catalog has it wrong, so we calculate
  // the y offset via height and radius

  if (!prpToLrp || !nearPortion) {
    return null
  }
  
  if (
    nearPortion.shapeType === 'STRAIGHT' ||
    // shapeType is not saved in catalog yet, this is workaround until catalogs are updated
    // if bifocal has only bottom radius, it should mean it is straight
    (nearPortion.radius?.bottom && !nearPortion.radius?.top)
  ) {
    // top arc of bifocal visualization is straight
    // we need to set top radius to some big value
    nearPortion.radius.top = 10000
  }

  // our pupil (height + PD) is LRP, which is the center of drawing
  // TODO: prpToLrp.in is reversed
  const prp = { in: -prpToLrp.in, up: prpToLrp.up }

  const bottomYOffset = nearPortion.height - nearPortion.radius.bottom

  // TODO: spravne to tu odecist
  const bottomX = prp.in + prpToLrp.in
  const bottomY = bottomYOffset

  // where the highest point of top part is located
  const topHighestPointY = 0

  const conf = {
    prp,
    bottomX,
    bottomY,
    bottomR: nearPortion.radius.bottom,
    topX: bottomX,
    topY: topHighestPointY + nearPortion.radius.top,
    topR: nearPortion.radius.top,
  }

  const [p1, p2] = intersectTwoCircles(
    conf.bottomX,
    conf.bottomY,
    conf.bottomR,
    conf.topX,
    conf.topY,
    conf.topR,
  )

  if (!p1 || !p2) {
    return null
  }


  // delka tetivy
  const chordLength = Math.abs(p2.x - p1.x)

  const topAngle = Math.asin(chordLength / (2 * conf.topR)) * 2

  const bottomAngle = 2 * Math.PI - Math.asin(chordLength / (2 * conf.bottomR)) * 2

  // console.log(
  //   'getBifocalConfig',
  //   conf,
  //   [p1, p2],
  //   chordLength,
  //   topAngle,
  //   radiansToDegrees(topAngle),
  //   radiansToDegrees(bottomAngle),
  // )
  return {
    ...conf,
    topAngle,
    bottomAngle,
  }
}

// const pairKeysToClean = [
//   'addition',
//   'axis',
//   'prism1',
//   'base1',
//   'dbl',
//   'cylinder',
//   'smallestEnclosingCircle',
//   'pd',
//   'height',
// ]

// const singleKeysToClean = [
//   'dbl',
// ]

// // not tested and used yet
// export const cleanVcaFromOrderValues = vca => {
//   vca = { ...vca }
//   keysToClean.forEach(k => {
//     delete vca[k]
//   })
//   return vca
// }

export const findThicknessExtremes = data => {
  // const min = data.reduce((res, current) => res < current ? res : current, data[0])
  // const max = data.reduce((res, current) => res > current ? res : current, data[0])
  let min = data[0]
  let max = data[0]
  let minIndex = 0
  let maxIndex = 0

  data.forEach((d, index) => {
    if (d.thickness < min.thickness) {
      min = d
      minIndex = index
    }
    if (d.thickness > max.thickness) {
      max = d
      maxIndex = index
    }
  })

  return {
    min,
    max,
    minIndex,
    maxIndex,
  }
}

// (re)calculates extra data for VCA values
// orderValues are optional for better DM calculation
export function processVca(data, orderValues) {
  const { shapeData } = data

  if (
    (!shapeData || !shapeData.r || shapeData.r.length === 0) &&
    (!shapeData || !shapeData.l || shapeData.l.length === 0)
  ) {
    // no shape data means we have nothing to calculate...
    console.log('no shape data to process')
    return {
      ...data,
      shapeData: null,
    }
  }

  return {
    ...data,
    ...recalcVca({ vca: data, orderValues }),
    holes: processHoles(data.holes),
  }
}

export function processHoles(holes) {
  if (!holes) {
    return holes
  }
  return holes.map(h => {
    if (h.id) {
      return h
    }
    return {
      ...h,
      id: nanoid(),
    }
  })
}

// calculates extra data for 3d and thickness VCA values
export function processVcaPoints(data) {
  // data = {
  //   ...data,
  // }
  const d3 = {
    r: process3d(data.d3.r),
    l: process3d(data.d3.l),
  }
  const edgeThickness = !data.edgeThickness
    ? { r: [], l: [] }
    : {
      r: radiiToXY(data.edgeThickness.r),
      l: radiiToXY(data.edgeThickness.l),
    }
  return {
    ...data,
    d3,
    edgeThickness,
    holes: processHoles(data.holes),
  }
}

export function fullNormalize(drawingVca, orderValues) {
  // FULL normalization
  // needs to be centered, otherwise BOX wont work
  let newShapeXYR = centerXYData(drawingVca.shapeDataXY.r)
  let newShapeXYL = flipXYData(newShapeXYR)
  const newShapeRadiiR = xyToRadii(newShapeXYR)
  const newShapeRadiiL = xyToRadii(newShapeXYL)
  newShapeXYR = radiiToXY(newShapeRadiiR)
  newShapeXYL = radiiToXY(newShapeRadiiL)

  drawingVca = {
    ...drawingVca,
    invalidRadii: true,
    shapeDataXY: {
      ...drawingVca.shapeDataXY,
      r: newShapeXYR,
      l: newShapeXYL,
    },
    shapeData: {
      ...drawingVca.shapeData,
      r: newShapeRadiiR,
      l: newShapeRadiiL,
    },
  }
  return processVca(drawingVca, orderValues)
}

// excel (peters) formula for stock lenses - enclosing circle with fixed center
function calcStockRadii({ box, dbl, pdR, pdL, heightR, heightL, shapeDataXY, lensR, lensL }) {
  log.trace(
    'inputs for calcStockRadii box,dbl,pdR,pdL,heightR,heightL,shapeDataXY,lensR,lensL,',
    box,
    dbl,
    pdR,
    pdL,
    heightR,
    heightL,
    shapeDataXY,
    lensR,
    lensL,
  )

  const hbox = box.r.hbox || box.l.hbox
  const vbox = box.r.vbox || box.l.vbox

  // const multifocalOffsetX = -2.5 // - bgcToPrp.in + prpToLrp.in
  // const multifocalOffsetY = 2 // - bgcToPrp.up + prpToLrp.up
  // this is offset to pd/height (which is our relative center)
  let multifocalOffsetXR = 0
  let multifocalOffsetYR = 0
  let multifocalOffsetXL = 0
  let multifocalOffsetYL = 0
  // if (lensR && lensR.progressiveGeo) {
  if (lensR?.progressiveGeo) {
    // multifocalOffsetXR = -lensR.progressiveGeo.bgcToPrp.in + lensR.progressiveGeo.prpToLrp.in
    // multifocalOffsetYR = -lensR.progressiveGeo.bgcToPrp.up + lensR.progressiveGeo.prpToLrp.up
    const geo = lensR.progressiveGeo

    // progressive = pupil is LRP, so we need to find BGC (lens geom center)
    multifocalOffsetXR = -geo.bgcToPrp.in + geo.prpToLrp.in
    multifocalOffsetYR = -geo.bgcToPrp.up + geo.prpToLrp.up
  }

  if (lensR?.bifocalGeo) {
    const geo = lensR.bifocalGeo
    // bifocal = pupil is LRP, so we need to find BGC (lens geom center)
    multifocalOffsetXR = -geo.bgcToPrp.in - geo.prpToLrp.in
    multifocalOffsetYR = -geo.bgcToPrp.up + geo.prpToLrp.up
  }

  if (lensL?.progressiveGeo) {
    const geo = lensL.progressiveGeo
    multifocalOffsetXL = -geo.bgcToPrp.in + geo.prpToLrp.in
    multifocalOffsetYL = -geo.bgcToPrp.up + geo.prpToLrp.up
  }
  if (lensL?.bifocalGeo) {
    const geo = lensL.bifocalGeo
    // TODO: either bifocal or progressive sign of geo.prpToLrp.in is wrong
    multifocalOffsetXL = -geo.bgcToPrp.in - geo.prpToLrp.in
    multifocalOffsetYL = -geo.bgcToPrp.up + geo.prpToLrp.up
  }

  // get center offsets (real lense to pd/height)
  // --> where is geom center related to pd and height
  // this is used for drawings and for centration to 0,0 and calculating diam (below)
  const xR = hbox / 2 + dbl / 2 - parseFloat(pdR, 10) + multifocalOffsetXR
  const xL = hbox / 2 + dbl / 2 - parseFloat(pdL, 10) + multifocalOffsetXL

  const yR = vbox / 2 - parseFloat(heightR, 10) + multifocalOffsetYR
  const yL = vbox / 2 - parseFloat(heightL, 10) + multifocalOffsetYL

  // log.debug(
  //   'parseFloat(pdR, 10)',
  //   vbox,
  //   parseFloat(heightR),
  //   heightR,
  //   pdR,
  //   parseFloat(pdR, 10),
  //   dbl / 2,
  //   hbox / 2,
  //   parseFloat(heightR, 10),
  // )

  // petruv vlastni vypocet -- je ten spravny!
  // hledame nejmensi opsanou kruznici, ktera ma dany stred a obsahuje cely dany tvar
  // 1. we need to offset the XY shape data by decentration first
  // console.log('final shape offset ', xR, yR)
  // const shapeR = shapeDataXY.r.map(p => ({
  //   x: p.x - xR,
  //   y: p.y + yR,
  // }))
  // const shapeL = shapeDataXY.l.map(p => ({
  //   x: p.x + xL,
  //   y: p.y + yL,
  // }))

  // 2. center is 0,0, find the greatest distance to center from all points
  // const rR = shapeR.reduce((acc, curr) => {
  //   const d = Math.sqrt(curr.x ** 2 + curr.y ** 2)
  //   return d > acc ? d : acc
  // }, 0)

  const rR = shapeDataXY.r.reduce((acc, curr) => {
    const dx = xR - curr.x
    const dy = yR - curr.y

    const d = Math.sqrt(dx ** 2 + dy ** 2)
    return d > acc ? d : acc
  }, 0)

  const rL = shapeDataXY.r.reduce((acc, curr) => {
    const dx = xL - curr.x
    const dy = yL - curr.y

    const d = Math.sqrt(dx ** 2 + dy ** 2)
    return d > acc ? d : acc
  }, 0)

  // const rL = shapeL.reduce((acc, curr) => {
  //   const d = Math.sqrt(curr.x ** 2 + curr.y ** 2)
  //   return d > acc ? d : acc
  // }, 0)

  return {
    xR: lensR ? xR : null,
    xL: lensL ? xL : null,
    yR: lensR ? yR : null,
    yL: lensL ? yL : null,
    rR: !lensR ? null : rR > 0 ? rR : 0,
    rL: !lensL ? null : rL > 0 ? rL : 0,
  }
}

// pick correct shape radius by lens catalog props
// this is used for:
// - step 2 - show shape radius
// - validation step 2 (and order sending), that catalog lens is big enough
// - filtering compare products in step 3
// - prepare order to send (create DMs from this)
// - get prices for reize
export function getFinalRadiiByShapeAndLenses({ vca, lensR, lensL, order }) {
  if (!vca) {
    return {}
  }

  if (!lensR || !lensL) {
    log.warn('lens is not defined in getFinalRadiiByShapeAndLenses', lensR, lensL, vca)
    // console.notifyWarning('lens is not defined in getFinalRadiiByShapeAndLenses', {
    //   msg: 'lens is not defined in getFinalRadiiByShapeAndLenses',
    // })
  }

  // first recalc vca for everything stateless (without order data)
  vca = recalcVca({ vca })
  const {
    smallestEnclosingCircleRadius,
    smallestEnclosingCircleX,
    smallestEnclosingCircleY,
    shapeDataXY,
    box,
  } = vca

  let stockRadii = null

  // with fallbacks to vca centration data
  const centrationValues = {
    pdR: order.pdR || vca.pdR,
    pdL: order.pdL || vca.pdL,
    heightR: order.heightR || vca.heightR,
    heightL: order.heightL || vca.heightL,
  }

  // only calc if we have centrationValues
  if (
    (centrationValues.pdR || centrationValues.pdL) &&
    (centrationValues.heightR || centrationValues.heightL)
  ) {
    // this is for stock lenses (designShift = 0)
    stockRadii = calcStockRadii({
      ...vca,
      ...centrationValues,
      shapeDataXY,
      box,
      lensR,
      lensL,
    })
  }

  // get whether is stock
  const isStockLensR = !!(lensR && lensR.stockLens)
  const isStockLensL = !!(lensL && lensL.stockLens)

  console.log('isStockLensR', isStockLensR)

  console.log(
    'lens has designShift',
    lensR && lensR.designShift,
    lensL && lensL.designShift,
    !lensR && 'NO LENSE!!!',
  )

  let useStockFormulaR = isStockLensR
  let useStockFormulaL = isStockLensL

  // TODO: maybe move this to apptype config as function
  // reize only (at least for now...)
  // TODO: nemělo by toto být zapnuto i pro JZO???

  // console.log('>>>>>>>lensR.designShift', lensR.designShift, order.isCtoCapableR, order.ctoR)

  if (appTypeConfig.APP_TYPE === 'reize') {
    // if designShift === 0, we use stock formula OR user can select rx-design (isCtoCapable) but he does NOT!
    useStockFormulaR = (lensR && !lensR.designShift) || (order.isCtoCapableR && !order.ctoR)
    useStockFormulaL = (lensL && !lensL.designShift) || (order.isCtoCapableL && !order.ctoL)
  }

  if (appTypeConfig.APP_TYPE === 'jzo') {
    useStockFormulaR = order.isCtoCapableR && !order.ctoR
    useStockFormulaL = order.isCtoCapableL && !order.ctoL
  }

  if (appTypeConfig.APP_TYPE === 'omega') {
    useStockFormulaR = isStockLensR || !getIsOmegaLensCtoCapable(lensR)
    useStockFormulaL = isStockLensL || !getIsOmegaLensCtoCapable(lensL)
  }

  // now we know, if we use stock formula, we need to decide what margin we use
  let marginR = isStockLensR
    ? appTypeConfig.safetyMarginStock
    : appTypeConfig.safetyMarginManufacturer
  let marginL = isStockLensL
    ? appTypeConfig.safetyMarginStock
    : appTypeConfig.safetyMarginManufacturer

  // of course with reize, it is not that easy
  if (appTypeConfig.APP_TYPE === 'reize') {
    // if rx-design checked OR is hidden (isCtoCapable)  use manufacturer
    // otherwise (stockLens has no rx-design neither ctoCapable OR it is capable but not checked)

    console.log(
      'order.ctoR ',
      order.ctoR,
      order.ctoL,
      order.isCtoCapableR,
      '-',
      order.isCtoCapableL,
    )
    marginR =
      order.ctoR || !order.isCtoCapableR
        ? appTypeConfig.safetyMarginManufacturer
        : appTypeConfig.safetyMarginStock
    marginL =
      order.ctoL || !order.isCtoCapableL
        ? appTypeConfig.safetyMarginManufacturer
        : appTypeConfig.safetyMarginStock
  }

  console.log('use margin', marginR, marginL)
  console.log('use stock formula', useStockFormulaR, useStockFormulaL)

  // RX manufacturer shape
  let r = smallestEnclosingCircleRadius + marginR
  let l = smallestEnclosingCircleRadius + marginL
  let isRxUsedR = true
  let isRxUsedL = true

  // if stock formula, then use the bigger one
  if (stockRadii && useStockFormulaR) {
    r = Math.max(stockRadii.rR + marginR, smallestEnclosingCircleRadius + marginR)
    isRxUsedR = false
  }
  if (stockRadii && useStockFormulaL) {
    l = Math.max(stockRadii.rL + marginL, smallestEnclosingCircleRadius + marginL)
    isRxUsedL = false
  }

  // finally assemble whole final object with correct centers and radii
  const final = {
    rL: l,
    rR: r,
    xL: isRxUsedL ? smallestEnclosingCircleX : stockRadii.xL,
    xR: isRxUsedR ? smallestEnclosingCircleX : stockRadii.xR,
    yL: isRxUsedL ? smallestEnclosingCircleY : stockRadii.yL,
    yR: isRxUsedR ? smallestEnclosingCircleY : stockRadii.yR,
  }

  console.log('isRxUsedR', isRxUsedR, 'false is stock ')
  console.log('isRxUsedL', isRxUsedL)

  // TODO: mozna nadradit vyber rx-design step 1 design shiftu???

  /**
   * SAFETY_MARGIN_STD=1.0 / means every lens without RX-Design get this value for radius (1.0mm)
   *    even products where RX-Design is available but it is unselected.
   * SAFETY_MARGIN_CTO=2.0 / mean for every lens where RX-Design is selected this margin
   *    will be taken for radius (2.0mm).
   */

  // if we have no lens, delete calculation!
  if (!lensR) {
    delete final.xR
    delete final.yR
    delete final.rR
    r = null
  }
  if (!lensL) {
    delete final.xL
    delete final.yL
    delete final.rL
    l = null
  }

  const radiiResults = {
    r,
    l,
    b: Math.max(r, l),
    final,
    stockRadiiWithoutMargin: stockRadii,
    smallestEnclosingCircleWithoutMargin: {
      radius: smallestEnclosingCircleRadius,
      x: smallestEnclosingCircleX,
      y: smallestEnclosingCircleY,
    },
  }

  log.trace('final radius radiiResults', radiiResults, r, l, radiiResults.b, radiiResults.b * 2)

  return radiiResults
}

// returns true iff the line from (a,b)->(c,d) intersects with (p,q)->(r,s)
function intersects(a, b, c, d, p, q, r, s) {
  let det
  let gamma
  let lambda
  det = (c - a) * (s - q) - (r - p) * (d - b)
  if (det === 0) {
    return false
  }
  lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det
  gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det
  return lambda > 0 && lambda < 1 && gamma > 0 && gamma < 1
}

function intersectsWhere(a, b, c, d, p, q, r, s) {
  let det
  let gamma
  let lambda
  det = (c - a) * (s - q) - (r - p) * (d - b)
  if (det === 0) {
    return false
  }
  lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det
  gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det
  // return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1);
  return {
    lambda,
    gamma,
  }
}

// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments
// Return FALSE if the lines don't intersect
function intersectWhereNew(x1, y1, x2, y2, x3, y3, x4, y4, index) {
  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    if (index === 29) {
      console.log('intersect log111', x1, y1, x2, y2, x3, y3, x4, y4)
    }
    return false
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1)

  // Lines are parallel
  if (denominator === 0) {
    if (index === 29) {
      console.log('intersect log2', x1, y1, x2, y2, x3, y3, x4, y4)
    }
    return false
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator

  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    if (index === 29) {
      console.log('intersect log3', x1, y1, x2, y2, x3, y3, x4, y4)
    }

    return false
  }

  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * (x2 - x1)
  const y = y1 + ua * (y2 - y1)

  if (index === 29) {
    console.log('intersect log', x, y)
  }

  return { x, y }
}

const defaultDrawingVca = {
  shapeData: {
    r: [
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.02,
      20.01,
      20.01,
      20.02,
      20.02,
      20.01,
      20.01,
      20.02,
      20.02,
      20.01,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20,
      20.01,
      20,
      20.01,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.03,
      20.02,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.03,
      20.03,
      20.04,
      20.04,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.02,
      20.02,
      20.03,
      20.03,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.02,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20,
      20.01,
      20,
      20,
      20,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.98,
      19.99,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.95,
      19.95,
      19.96,
      19.96,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.96,
      19.95,
      19.95,
      19.96,
      19.96,
      19.95,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      20,
      19.99,
      20,
      20,
      19.99,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20.01,
      20.01,
    ],
    l: [
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.02,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.03,
      20.03,
      20.02,
      20.02,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.04,
      20.04,
      20.03,
      20.03,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.04,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.03,
      20.02,
      20.03,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20.01,
      20,
      20.01,
      20,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.02,
      20.01,
      20.01,
      20.02,
      20.02,
      20.01,
      20.01,
      20.02,
      20.02,
      20.01,
      20.01,
      20.02,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20.01,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      20,
      20,
      20,
      20,
      19.99,
      20,
      20,
      19.99,
      20,
      19.99,
      20,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.99,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.95,
      19.96,
      19.96,
      19.95,
      19.95,
      19.96,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.95,
      19.96,
      19.96,
      19.95,
      19.95,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.96,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.97,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.98,
      19.99,
      19.98,
      19.99,
      19.99,
      19.99,
      19.99,
      20,
      19.99,
      20,
      20,
      20,
      20.01,
      20,
      20.01,
    ],
    // "r": [25.78, 25.8, 25.83, 25.87, 25.9, 25.92, 25.95, 25.98, 26.01, 26.04, 26.08, 26.11, 26.15, 26.19, 26.22, 26.26, 26.29, 26.32, 26.36, 26.41, 26.44, 26.48, 26.51, 26.55, 26.59, 26.63, 26.67, 26.72, 26.76, 26.8, 26.85, 26.9, 26.95, 26.99, 27.04, 27.08, 27.13, 27.18, 27.22, 27.26, 27.31, 27.36, 27.41, 27.45, 27.5, 27.56, 27.61, 27.66, 27.71, 27.76, 27.82, 27.88, 27.93, 27.98, 28.03, 28.09, 28.15, 28.2, 28.24, 28.3, 28.36, 28.41, 28.46, 28.5, 28.54, 28.59, 28.63, 28.67, 28.71, 28.73, 28.75, 28.77, 28.78, 28.79, 28.8, 28.8, 28.8, 28.79, 28.79, 28.77, 28.74, 28.69, 28.63, 28.58, 28.53, 28.47, 28.39, 28.3, 28.19, 28.08, 27.97, 27.85, 27.73, 27.61, 27.49, 27.36, 27.22, 27.09, 26.97, 26.84, 26.7, 26.56, 26.43, 26.3, 26.17, 26.04, 25.91, 25.78, 25.65, 25.52, 25.39, 25.26, 25.13, 25.01, 24.89, 24.76, 24.65, 24.53, 24.41, 24.28, 24.15, 24.03, 23.92, 23.81, 23.7, 23.59, 23.48, 23.37, 23.26, 23.15, 23.03, 22.93, 22.83, 22.73, 22.63, 22.53, 22.43, 22.33, 22.23, 22.14, 22.05, 21.95, 21.87, 21.78, 21.69, 21.6, 21.51, 21.42, 21.34, 21.26, 21.19, 21.12, 21.03, 20.96, 20.89, 20.8, 20.73, 20.66, 20.58, 20.51, 20.44, 20.38, 20.32, 20.25, 20.18, 20.11, 20.05, 19.98, 19.92, 19.86, 19.8, 19.74, 19.69, 19.63, 19.57, 19.51, 19.46, 19.41, 19.36, 19.31, 19.26, 19.22, 19.16, 19.11, 19.07, 19.03, 18.98, 18.93, 18.89, 18.84, 18.8, 18.76, 18.71, 18.67, 18.63, 18.59, 18.56, 18.52, 18.48, 18.44, 18.41, 18.37, 18.34, 18.3, 18.27, 18.23, 18.21, 18.18, 18.15, 18.12, 18.09, 18.06, 18.03, 18, 17.96, 17.94, 17.92, 17.89, 17.87, 17.85, 17.83, 17.81, 17.79, 17.77, 17.75, 17.73, 17.71, 17.69, 17.68, 17.67, 17.65, 17.63, 17.62, 17.6, 17.59, 17.58, 17.56, 17.55, 17.54, 17.53, 17.51, 17.5, 17.49, 17.49, 17.48, 17.47, 17.46, 17.45, 17.45, 17.45, 17.44, 17.44, 17.43, 17.43, 17.42, 17.42, 17.42, 17.42, 17.42, 17.42, 17.41, 17.41, 17.41, 17.41, 17.41, 17.41, 17.42, 17.42, 17.42, 17.43, 17.44, 17.45, 17.46, 17.46, 17.47, 17.48, 17.48, 17.49, 17.5, 17.51, 17.53, 17.54, 17.55, 17.56, 17.57, 17.59, 17.61, 17.62, 17.64, 17.65, 17.67, 17.69, 17.71, 17.73, 17.75, 17.77, 17.79, 17.81, 17.83, 17.86, 17.88, 17.9, 17.93, 17.96, 17.99, 18.02, 18.04, 18.07, 18.1, 18.13, 18.16, 18.18, 18.21, 18.25, 18.28, 18.31, 18.34, 18.37, 18.41, 18.44, 18.48, 18.52, 18.55, 18.59, 18.62, 18.67, 18.71, 18.76, 18.8, 18.84, 18.88, 18.92, 18.97, 19.01, 19.06, 19.1, 19.15, 19.2, 19.25, 19.29, 19.34, 19.4, 19.46, 19.51, 19.56, 19.62, 19.67, 19.72, 19.78, 19.84, 19.9, 19.96, 20.01, 20.08, 20.15, 20.21, 20.27, 20.34, 20.41, 20.48, 20.54, 20.62, 20.69, 20.77, 20.84, 20.92, 21, 21.08, 21.16, 21.24, 21.33, 21.41, 21.48, 21.57, 21.66, 21.74, 21.83, 21.92, 22.02, 22.11, 22.2, 22.3, 22.4, 22.49, 22.59, 22.69, 22.79, 22.9, 23, 23.11, 23.21, 23.31, 23.42, 23.53, 23.64, 23.76, 23.86, 23.98, 24.1, 24.21, 24.32, 24.44, 24.55, 24.66, 24.77, 24.9, 25.01, 25.14, 25.26, 25.39, 25.51, 25.63, 25.76, 25.88, 26, 26.11, 26.23, 26.34, 26.46, 26.57, 26.68, 26.79, 26.9, 27, 27.09, 27.18, 27.28, 27.36, 27.44, 27.51, 27.58, 27.64, 27.7, 27.74, 27.78, 27.8, 27.81, 27.81, 27.81, 27.8, 27.78, 27.77, 27.76, 27.74, 27.72, 27.7, 27.68, 27.65, 27.62, 27.59, 27.57, 27.53, 27.5, 27.46, 27.43, 27.4, 27.37, 27.33, 27.29, 27.26, 27.24, 27.2, 27.17, 27.13, 27.09, 27.06, 27.04, 27.01, 26.98, 26.95, 26.92, 26.89, 26.86, 26.83, 26.8, 26.78, 26.75, 26.72, 26.7, 26.67, 26.65, 26.62, 26.6, 26.58, 26.55, 26.53, 26.51, 26.49, 26.47, 26.45, 26.43, 26.41, 26.4, 26.39, 26.38, 26.36, 26.35, 26.34, 26.33, 26.32, 26.3, 26.28, 26.27, 26.27, 26.28, 26.28, 26.27, 26.27, 26.27, 26.26, 26.26, 26.26, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.24, 26.25, 26.25, 26.25, 26.25, 26.26, 26.27, 26.27, 26.27, 26.28, 26.3, 26.31, 26.32, 26.33, 26.34, 26.36, 26.38, 26.39, 26.4, 26.41, 26.43, 26.45, 26.46, 26.48, 26.5, 26.52, 26.55, 26.58, 26.6, 26.62, 26.64, 26.66, 26.68, 26.7, 26.72, 26.74, 26.76, 26.79, 26.82, 26.85, 26.88, 26.9, 26.93, 26.96, 26.99, 27.01, 27.03, 27.06, 27.08, 27.11, 27.13, 27.15, 27.17, 27.18, 27.19, 27.2, 27.2, 27.21, 27.21, 27.21, 27.21, 27.2, 27.19, 27.18, 27.16, 27.14, 27.11, 27.07, 27.01, 26.94, 26.87, 26.8, 26.72, 26.64, 26.55, 26.46, 26.36, 26.25, 26.14, 26.04, 25.94, 25.83, 25.72, 25.61, 25.5, 25.4, 25.28, 25.17, 25.07, 24.96, 24.86, 24.75, 24.64, 24.52, 24.41, 24.31, 24.21, 24.1, 23.99, 23.89, 23.78, 23.67, 23.57, 23.46, 23.36, 23.28, 23.17, 23.07, 22.97, 22.87, 22.78, 22.68, 22.58, 22.48, 22.39, 22.3, 22.2, 22.11, 22.03, 21.95, 21.86, 21.77, 21.68, 21.6, 21.51, 21.43, 21.35, 21.26, 21.18, 21.11, 21.03, 20.96, 20.89, 20.81, 20.73, 20.66, 20.59, 20.51, 20.43, 20.36, 20.29, 20.22, 20.16, 20.1, 20.03, 19.97, 19.91, 19.84, 19.78, 19.72, 19.65, 19.59, 19.54, 19.48, 19.43, 19.37, 19.31, 19.26, 19.21, 19.16, 19.1, 19.05, 19.01, 18.97, 18.92, 18.86, 18.82, 18.78, 18.74, 18.7, 18.66, 18.62, 18.58, 18.54, 18.51, 18.47, 18.44, 18.4, 18.37, 18.34, 18.31, 18.28, 18.25, 18.21, 18.18, 18.15, 18.13, 18.1, 18.07, 18.04, 18.02, 18, 17.97, 17.94, 17.92, 17.9, 17.88, 17.85, 17.83, 17.82, 17.8, 17.78, 17.77, 17.75, 17.73, 17.72, 17.7, 17.69, 17.67, 17.66, 17.65, 17.64, 17.63, 17.62, 17.61, 17.6, 17.59, 17.59, 17.59, 17.58, 17.57, 17.56, 17.55, 17.54, 17.53, 17.53, 17.53, 17.52, 17.52, 17.51, 17.51, 17.51, 17.5, 17.5, 17.51, 17.5, 17.5, 17.5, 17.5, 17.51, 17.51, 17.51, 17.51, 17.51, 17.51, 17.51, 17.52, 17.52, 17.52, 17.52, 17.53, 17.54, 17.55, 17.56, 17.57, 17.58, 17.6, 17.61, 17.62, 17.63, 17.65, 17.66, 17.67, 17.69, 17.7, 17.72, 17.74, 17.75, 17.77, 17.79, 17.82, 17.84, 17.85, 17.87, 17.9, 17.92, 17.94, 17.96, 18, 18.03, 18.05, 18.08, 18.11, 18.15, 18.18, 18.2, 18.25, 18.29, 18.33, 18.36, 18.38, 18.41, 18.45, 18.49, 18.52, 18.56, 18.59, 18.63, 18.67, 18.71, 18.74, 18.79, 18.83, 18.87, 18.91, 18.96, 19, 19.04, 19.08, 19.13, 19.18, 19.23, 19.28, 19.33, 19.37, 19.43, 19.49, 19.54, 19.58, 19.63, 19.68, 19.74, 19.79, 19.84, 19.89, 19.95, 20.01, 20.08, 20.14, 20.19, 20.25, 20.31, 20.37, 20.43, 20.5, 20.56, 20.62, 20.68, 20.74, 20.8, 20.88, 20.95, 21.02, 21.09, 21.16, 21.22, 21.29, 21.36, 21.44, 21.51, 21.58, 21.65, 21.73, 21.81, 21.89, 21.96, 22.04, 22.13, 22.21, 22.28, 22.36, 22.45, 22.53, 22.61, 22.69, 22.78, 22.86, 22.94, 23.02, 23.11, 23.19, 23.26, 23.34, 23.42, 23.5, 23.58, 23.65, 23.72, 23.8, 23.88, 23.95, 24.02, 24.09, 24.15, 24.22, 24.28, 24.34, 24.41, 24.47, 24.52, 24.58, 24.62, 24.67, 24.71, 24.75, 24.78, 24.81, 24.85, 24.88, 24.91, 24.93, 24.95, 24.96, 24.97, 24.98, 25, 25, 25.01, 25.01, 25.02, 25.03, 25.04, 25.03, 25.03, 25.04, 25.04, 25.04, 25.04, 25.04, 25.04, 25.04, 25.05, 25.04, 25.04, 25.05, 25.05, 25.06, 25.05, 25.05, 25.05, 25.06, 25.07, 25.06, 25.06, 25.06, 25.08, 25.08, 25.09, 25.09, 25.09, 25.1, 25.11, 25.12, 25.12, 25.12, 25.13, 25.14, 25.15, 25.15, 25.16, 25.18, 25.2, 25.21, 25.22, 25.23, 25.25, 25.27, 25.28, 25.29, 25.3, 25.32, 25.34, 25.36, 25.38, 25.39, 25.41, 25.43, 25.45, 25.47, 25.49, 25.51, 25.53, 25.56, 25.59, 25.61, 25.63, 25.66, 25.69, 25.72, 25.75],
    // "r": [25.78, 26.08, 26.44, 26.85, 27.31, 27.82, 28.36, 28.75, 28.74, 27.97, 26.7, 25.39, 24.15, 23.03, 22.05, 21.19, 20.44, 19.8, 19.26, 18.8, 18.41, 18.09, 17.83, 17.65, 17.51, 17.44, 17.41, 17.44, 17.53, 17.67, 17.88, 18.16, 18.48, 18.88, 19.34, 19.9, 20.54, 21.33, 22.2, 23.21, 24.32, 25.51, 26.68, 27.58, 27.78, 27.57, 27.24, 26.92, 26.65, 26.43, 26.3, 26.26, 26.24, 26.3, 26.43, 26.64, 26.88, 27.13, 27.21, 26.87, 25.94, 24.86, 23.78, 22.78, 21.86, 21.03, 20.29, 19.65, 19.1, 18.66, 18.31, 18.02, 17.8, 17.65, 17.57, 17.51, 17.51, 17.53, 17.65, 17.82, 18.05, 18.38, 18.74, 19.18, 19.68, 20.25, 20.88, 21.58, 22.36, 23.19, 23.95, 24.58, 24.93, 25.03, 25.04, 25.06, 25.1, 25.18, 25.32, 25.51],
    // "l": [26.3, 26.32, 26.33, 26.34, 26.35, 26.36, 26.38, 26.39, 26.4, 26.41, 26.43, 26.45, 26.47, 26.49, 26.51, 26.53, 26.55, 26.58, 26.6, 26.62, 26.65, 26.67, 26.7, 26.72, 26.75, 26.78, 26.8, 26.83, 26.86, 26.89, 26.92, 26.95, 26.98, 27.01, 27.04, 27.06, 27.09, 27.13, 27.17, 27.2, 27.24, 27.26, 27.29, 27.33, 27.37, 27.4, 27.43, 27.46, 27.5, 27.53, 27.57, 27.59, 27.62, 27.65, 27.68, 27.7, 27.72, 27.74, 27.76, 27.77, 27.78, 27.8, 27.81, 27.81, 27.81, 27.8, 27.78, 27.74, 27.7, 27.64, 27.58, 27.51, 27.44, 27.36, 27.28, 27.18, 27.09, 27, 26.9, 26.79, 26.68, 26.57, 26.46, 26.34, 26.23, 26.11, 26, 25.88, 25.76, 25.63, 25.51, 25.39, 25.26, 25.14, 25.01, 24.9, 24.77, 24.66, 24.55, 24.44, 24.32, 24.21, 24.1, 23.98, 23.86, 23.76, 23.64, 23.53, 23.42, 23.31, 23.21, 23.11, 23, 22.9, 22.79, 22.69, 22.59, 22.49, 22.4, 22.3, 22.2, 22.11, 22.02, 21.92, 21.83, 21.74, 21.66, 21.57, 21.48, 21.41, 21.33, 21.24, 21.16, 21.08, 21, 20.92, 20.84, 20.77, 20.69, 20.62, 20.54, 20.48, 20.41, 20.34, 20.27, 20.21, 20.15, 20.08, 20.01, 19.96, 19.9, 19.84, 19.78, 19.72, 19.67, 19.62, 19.56, 19.51, 19.46, 19.4, 19.34, 19.29, 19.25, 19.2, 19.15, 19.1, 19.06, 19.01, 18.97, 18.92, 18.88, 18.84, 18.8, 18.76, 18.71, 18.67, 18.62, 18.59, 18.55, 18.52, 18.48, 18.44, 18.41, 18.37, 18.34, 18.31, 18.28, 18.25, 18.21, 18.18, 18.16, 18.13, 18.1, 18.07, 18.04, 18.02, 17.99, 17.96, 17.93, 17.9, 17.88, 17.86, 17.83, 17.81, 17.79, 17.77, 17.75, 17.73, 17.71, 17.69, 17.67, 17.65, 17.64, 17.62, 17.61, 17.59, 17.57, 17.56, 17.55, 17.54, 17.53, 17.51, 17.5, 17.49, 17.48, 17.48, 17.47, 17.46, 17.46, 17.45, 17.44, 17.43, 17.42, 17.42, 17.42, 17.41, 17.41, 17.41, 17.41, 17.41, 17.41, 17.42, 17.42, 17.42, 17.42, 17.42, 17.42, 17.43, 17.43, 17.44, 17.44, 17.45, 17.45, 17.45, 17.46, 17.47, 17.48, 17.49, 17.49, 17.5, 17.51, 17.53, 17.54, 17.55, 17.56, 17.58, 17.59, 17.6, 17.62, 17.63, 17.65, 17.67, 17.68, 17.69, 17.71, 17.73, 17.75, 17.77, 17.79, 17.81, 17.83, 17.85, 17.87, 17.89, 17.92, 17.94, 17.96, 18, 18.03, 18.06, 18.09, 18.12, 18.15, 18.18, 18.21, 18.23, 18.27, 18.3, 18.34, 18.37, 18.41, 18.44, 18.48, 18.52, 18.56, 18.59, 18.63, 18.67, 18.71, 18.76, 18.8, 18.84, 18.89, 18.93, 18.98, 19.03, 19.07, 19.11, 19.16, 19.22, 19.26, 19.31, 19.36, 19.41, 19.46, 19.51, 19.57, 19.63, 19.69, 19.74, 19.8, 19.86, 19.92, 19.98, 20.05, 20.11, 20.18, 20.25, 20.32, 20.38, 20.44, 20.51, 20.58, 20.66, 20.73, 20.8, 20.89, 20.96, 21.03, 21.12, 21.19, 21.26, 21.34, 21.42, 21.51, 21.6, 21.69, 21.78, 21.87, 21.95, 22.05, 22.14, 22.23, 22.33, 22.43, 22.53, 22.63, 22.73, 22.83, 22.93, 23.03, 23.15, 23.26, 23.37, 23.48, 23.59, 23.7, 23.81, 23.92, 24.03, 24.15, 24.28, 24.41, 24.53, 24.65, 24.76, 24.89, 25.01, 25.13, 25.26, 25.39, 25.52, 25.65, 25.78, 25.91, 26.04, 26.17, 26.3, 26.43, 26.56, 26.7, 26.84, 26.97, 27.09, 27.22, 27.36, 27.49, 27.61, 27.73, 27.85, 27.97, 28.08, 28.19, 28.3, 28.39, 28.47, 28.53, 28.58, 28.63, 28.69, 28.74, 28.77, 28.79, 28.79, 28.8, 28.8, 28.8, 28.79, 28.78, 28.77, 28.75, 28.73, 28.71, 28.67, 28.63, 28.59, 28.54, 28.5, 28.46, 28.41, 28.36, 28.3, 28.24, 28.2, 28.15, 28.09, 28.03, 27.98, 27.93, 27.88, 27.82, 27.76, 27.71, 27.66, 27.61, 27.56, 27.5, 27.45, 27.41, 27.36, 27.31, 27.26, 27.22, 27.18, 27.13, 27.08, 27.04, 26.99, 26.95, 26.9, 26.85, 26.8, 26.76, 26.72, 26.67, 26.63, 26.59, 26.55, 26.51, 26.48, 26.44, 26.41, 26.36, 26.32, 26.29, 26.26, 26.22, 26.19, 26.15, 26.11, 26.08, 26.04, 26.01, 25.98, 25.95, 25.92, 25.9, 25.87, 25.83, 25.8, 25.78, 25.75, 25.72, 25.69, 25.66, 25.63, 25.61, 25.59, 25.56, 25.53, 25.51, 25.49, 25.47, 25.45, 25.43, 25.41, 25.39, 25.38, 25.36, 25.34, 25.32, 25.3, 25.29, 25.28, 25.27, 25.25, 25.23, 25.22, 25.21, 25.2, 25.18, 25.16, 25.15, 25.15, 25.14, 25.13, 25.12, 25.12, 25.12, 25.11, 25.1, 25.09, 25.09, 25.09, 25.08, 25.08, 25.06, 25.06, 25.06, 25.07, 25.06, 25.05, 25.05, 25.05, 25.06, 25.05, 25.05, 25.04, 25.04, 25.05, 25.04, 25.04, 25.04, 25.04, 25.04, 25.04, 25.04, 25.03, 25.03, 25.04, 25.03, 25.02, 25.01, 25.01, 25, 25, 24.98, 24.97, 24.96, 24.95, 24.93, 24.91, 24.88, 24.85, 24.81, 24.78, 24.75, 24.71, 24.67, 24.62, 24.58, 24.52, 24.47, 24.41, 24.34, 24.28, 24.22, 24.15, 24.09, 24.02, 23.95, 23.88, 23.8, 23.72, 23.65, 23.58, 23.5, 23.42, 23.34, 23.26, 23.19, 23.11, 23.02, 22.94, 22.86, 22.78, 22.69, 22.61, 22.53, 22.45, 22.36, 22.28, 22.21, 22.13, 22.04, 21.96, 21.89, 21.81, 21.73, 21.65, 21.58, 21.51, 21.44, 21.36, 21.29, 21.22, 21.16, 21.09, 21.02, 20.95, 20.88, 20.8, 20.74, 20.68, 20.62, 20.56, 20.5, 20.43, 20.37, 20.31, 20.25, 20.19, 20.14, 20.08, 20.01, 19.95, 19.89, 19.84, 19.79, 19.74, 19.68, 19.63, 19.58, 19.54, 19.49, 19.43, 19.37, 19.33, 19.28, 19.23, 19.18, 19.13, 19.08, 19.04, 19, 18.96, 18.91, 18.87, 18.83, 18.79, 18.74, 18.71, 18.67, 18.63, 18.59, 18.56, 18.52, 18.49, 18.45, 18.41, 18.38, 18.36, 18.33, 18.29, 18.25, 18.2, 18.18, 18.15, 18.11, 18.08, 18.05, 18.03, 18, 17.96, 17.94, 17.92, 17.9, 17.87, 17.85, 17.84, 17.82, 17.79, 17.77, 17.75, 17.74, 17.72, 17.7, 17.69, 17.67, 17.66, 17.65, 17.63, 17.62, 17.61, 17.6, 17.58, 17.57, 17.56, 17.55, 17.54, 17.53, 17.52, 17.52, 17.52, 17.52, 17.51, 17.51, 17.51, 17.51, 17.51, 17.51, 17.51, 17.5, 17.5, 17.5, 17.5, 17.51, 17.5, 17.5, 17.51, 17.51, 17.51, 17.52, 17.52, 17.53, 17.53, 17.53, 17.54, 17.55, 17.56, 17.57, 17.58, 17.59, 17.59, 17.59, 17.6, 17.61, 17.62, 17.63, 17.64, 17.65, 17.66, 17.67, 17.69, 17.7, 17.72, 17.73, 17.75, 17.77, 17.78, 17.8, 17.82, 17.83, 17.85, 17.88, 17.9, 17.92, 17.94, 17.97, 18, 18.02, 18.04, 18.07, 18.1, 18.13, 18.15, 18.18, 18.21, 18.25, 18.28, 18.31, 18.34, 18.37, 18.4, 18.44, 18.47, 18.51, 18.54, 18.58, 18.62, 18.66, 18.7, 18.74, 18.78, 18.82, 18.86, 18.92, 18.97, 19.01, 19.05, 19.1, 19.16, 19.21, 19.26, 19.31, 19.37, 19.43, 19.48, 19.54, 19.59, 19.65, 19.72, 19.78, 19.84, 19.91, 19.97, 20.03, 20.1, 20.16, 20.22, 20.29, 20.36, 20.43, 20.51, 20.59, 20.66, 20.73, 20.81, 20.89, 20.96, 21.03, 21.11, 21.18, 21.26, 21.35, 21.43, 21.51, 21.6, 21.68, 21.77, 21.86, 21.95, 22.03, 22.11, 22.2, 22.3, 22.39, 22.48, 22.58, 22.68, 22.78, 22.87, 22.97, 23.07, 23.17, 23.28, 23.36, 23.46, 23.57, 23.67, 23.78, 23.89, 23.99, 24.1, 24.21, 24.31, 24.41, 24.52, 24.64, 24.75, 24.86, 24.96, 25.07, 25.17, 25.28, 25.4, 25.5, 25.61, 25.72, 25.83, 25.94, 26.04, 26.14, 26.25, 26.36, 26.46, 26.55, 26.64, 26.72, 26.8, 26.87, 26.94, 27.01, 27.07, 27.11, 27.14, 27.16, 27.18, 27.19, 27.2, 27.21, 27.21, 27.21, 27.21, 27.2, 27.2, 27.19, 27.18, 27.17, 27.15, 27.13, 27.11, 27.08, 27.06, 27.03, 27.01, 26.99, 26.96, 26.93, 26.9, 26.88, 26.85, 26.82, 26.79, 26.76, 26.74, 26.72, 26.7, 26.68, 26.66, 26.64, 26.62, 26.6, 26.58, 26.55, 26.52, 26.5, 26.48, 26.46, 26.45, 26.43, 26.41, 26.4, 26.39, 26.38, 26.36, 26.34, 26.33, 26.32, 26.31, 26.3, 26.28, 26.27, 26.27, 26.27, 26.26, 26.25, 26.25, 26.25, 26.25, 26.24, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.25, 26.26, 26.26, 26.26, 26.27, 26.27, 26.27, 26.28, 26.28, 26.27, 26.27, 26.28],
    initialLineL: '1;512;E;L;F',
    initialLineR: '1;512;E;R;F',
  },
  dbl: 18,
  bevelModifier: 0,
  holes: [],
}
export function getDefaultRawDrawingVca() {
  return {
    ...defaultDrawingVca,
    shapeData: {
      ...defaultDrawingVca.shapeData,
      r: defaultDrawingVca.shapeData.r.filter((a, index) => index % 3 === 0), // 200
      // r: defaultDrawingVca.shapeData.r.filter((a, index) => index % 10 === 0), // 100
    },
  }
}

export function flipXYData(xyData) {
  const flippedShapeXY = xyData.map((p, i) => ({
    ...p,
    x: -p.x,
  }))

  const half = Math.floor(flippedShapeXY.length / 2)
  const threeHalfs = half * 3
  const newShapeXY = []
  for (let i = 0; i < flippedShapeXY.length; i++) {
    const p = flippedShapeXY[i]
    if (i <= half) {
      newShapeXY[half - i] = p
    } else {
      newShapeXY[threeHalfs - i] = p
    }
  }
  return newShapeXY
}

export function flipData(data) {
  // const quadrant = Math.floor(data.length / 4)
  const half = Math.floor(data.length / 2)
  let flipped = [...data]
  // for (let i = quadrant; i < 2 * quadrant; i++) {
  //   flipped.push(data[i])
  // }
  // for (let i = 0; i < quadrant; i++) {
  //   flipped.push(data[i])
  // }
  // for (let i = 3 * quadrant; i < 4 * quadrant; i++) {
  //   flipped.push(data[i])
  // }
  // for (let i = 2 * quadrant; i < 3 * quadrant; i++) {
  //   flipped.push(data[i])
  // }
  flipped = flipped.reverse()
  flipped = [...flipped.slice(half), ...flipped.slice(0, half)]

  return flipped
}
